/**
 * Searchable attribute combobox for policy editors
 * Groups attributes by XACML category with freeform fallback
 */

import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAllAttributes } from '@/hooks/use-attributes'
import type { Attribute, AttributeCategory } from '@/types/attribute'

interface AttributeComboboxProps {
  value: string
  onChange: (key: string, attribute: Attribute | undefined) => void
  className?: string
}

const CATEGORY_LABELS: Record<AttributeCategory, string> = {
  SUBJECT: 'Subject',
  RESOURCE: 'Resource',
  ACTION: 'Action',
  ENVIRONMENT: 'Environment',
}

const CATEGORY_ORDER: AttributeCategory[] = ['SUBJECT', 'RESOURCE', 'ACTION', 'ENVIRONMENT']

function extractShortKey(urnOrKey: string): string {
  if (urnOrKey.startsWith('urn:')) {
    const parts = urnOrKey.split(':')
    return parts[parts.length - 1]
  }
  return urnOrKey
}

export function AttributeCombobox({ value, onChange, className }: AttributeComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data: attributesData, isLoading } = useAllAttributes()
  const allAttributes = attributesData?.data ?? []

  const grouped = useMemo(() => {
    const groups: Record<AttributeCategory, Attribute[]> = {
      SUBJECT: [],
      RESOURCE: [],
      ACTION: [],
      ENVIRONMENT: [],
    }
    for (const attr of allAttributes) {
      groups[attr.category].push(attr)
    }
    return groups
  }, [allAttributes])

  const shortKey = extractShortKey(value)
  const selectedAttribute = allAttributes.find(
    (a) => a.key === value || a.key === shortKey
  )

  const displayLabel = selectedAttribute
    ? `${selectedAttribute.key} (${selectedAttribute.displayName})`
    : value || 'Select attribute...'

  const hasExactMatch = allAttributes.some(
    (a) => a.key.toLowerCase() === search.toLowerCase()
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 justify-between font-mono text-sm', className)}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search attributes..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? (
                'Loading attributes...'
              ) : search ? (
                <button
                  className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded cursor-pointer"
                  onClick={() => {
                    onChange(search, undefined)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  Use custom: <span className="font-mono font-medium">{search}</span>
                </button>
              ) : (
                'No attributes found.'
              )}
            </CommandEmpty>
            {CATEGORY_ORDER.map((category) => {
              const attrs = grouped[category]
              if (attrs.length === 0) return null
              return (
                <CommandGroup key={category} heading={CATEGORY_LABELS[category]}>
                  {attrs.map((attr) => (
                    <CommandItem
                      key={attr.id}
                      value={`${attr.key} ${attr.displayName}`}
                      onSelect={() => {
                        onChange(attr.key, attr)
                        setOpen(false)
                        setSearch('')
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          attr.key === value || attr.key === shortKey ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <span className="font-mono text-sm">{attr.key}</span>
                      <span className="ml-2 text-xs text-muted-foreground truncate">
                        {attr.displayName}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            })}
            {search && !hasExactMatch && allAttributes.length > 0 && (
              <CommandGroup heading="Custom">
                <CommandItem
                  value={`custom-${search}`}
                  onSelect={() => {
                    onChange(search, undefined)
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  Use custom: <span className="font-mono font-medium ml-1">{search}</span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
